<template>
  <div
    v-if="hasUpdate && !isCondensed"
    class="mx-2 p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm cursor-pointer"
    :title="t('update.confirmTitle')"
    @click="handleToggle"
  >
    <div v-if="!collapsed">
      <p class="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
        <svg class="w-5 h-5 text-brand-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        {{ t('update.confirmTitle') }}
        <span v-if="latestVersion" class="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
          (v{{ latestVersion }})
        </span>
      </p>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ t('update.confirmDescription') }}
      </p>
      <div class="mt-3 space-y-2">
        <BaseButton size="sm" class="w-full" :disabled="isUpdating" @click.stop="reloadApp">
          {{ t('update.updateNow') }}
        </BaseButton>
        <BaseButton size="sm" variant="ghost" class="w-full" @click.stop="collapsed = true">
          {{ t('update.later') }}
        </BaseButton>
      </div>
    </div>

    <div v-else class="flex items-center gap-2 w-full justify-center">
      <svg class="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
      <span class="text-sm font-semibold text-gray-900 dark:text-white">
        {{ t('update.confirmTitle') }}
        <span v-if="latestVersion" class="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
          (v{{ latestVersion }})
        </span>
      </span>
    </div>
  </div>

  <div
    v-if="hasUpdate && isCondensed"
    class="relative flex items-center justify-center p-2 cursor-pointer"
    :title="t('update.confirmTitle')"
    @click="collapsed = !collapsed"
  >
    <svg class="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
    <div
      v-if="!collapsed"
      class="absolute left-full top-0 z-50 ml-2 w-72 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-700 dark:bg-gray-900"
      @click.stop
    >
      <p class="text-sm font-semibold text-gray-900 dark:text-white">
        {{ t('update.confirmTitle') }}
        <span v-if="latestVersion" class="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
          (v{{ latestVersion }})
        </span>
      </p>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ t('update.confirmDescription') }}
      </p>
      <div class="mt-3 space-y-2">
        <BaseButton size="sm" class="w-full" :disabled="isUpdating" @click.stop="reloadApp">
          {{ t('update.updateNow') }}
        </BaseButton>
        <BaseButton size="sm" variant="ghost" class="w-full" @click.stop="collapsed = true">
          {{ t('update.later') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/base/BaseButton.vue'
import { useAppUpdate } from '@/composables/useAppUpdate'

interface Props {
  isCondensed?: boolean
}

withDefaults(defineProps<Props>(), {
  isCondensed: false,
})

const { hasUpdate, latestVersion, isUpdating, reloadApp } = useAppUpdate()
const { t } = useI18n()

const collapsed = ref(false)

function handleToggle(): void {
  if (collapsed.value) collapsed.value = false
}
</script>
